import { kindLabels } from '../events/api';
import {
  eventTriggerGroups,
  postSecondsOptions,
  preSecondsOptions,
  type EventSettings,
} from './api';

interface Props {
  value: EventSettings;
  onChange: (v: EventSettings) => void;
  readOnly: boolean;
  /** Field errors from the API, keyed "event.kinds", "event.pre_seconds"… */
  errors: Record<string, string>;
}

const seconds = (s: number) =>
  s >= 60 && s % 60 === 0 ? `${String(s / 60)} min` : `${String(s)} s`;

/** Which events start a recording, and how much before (copied from the device) and after (ADR-040, ADR-053). */
export function EventEditor({ value, onChange, readOnly, errors }: Props) {
  const toggle = (kind: string) => {
    const on = value.kinds.includes(kind);
    onChange({
      ...value,
      kinds: on ? value.kinds.filter((k) => k !== kind) : [...value.kinds, kind],
    });
  };

  return (
    <fieldset className="space-y-3 rounded-md border border-line p-3">
      <legend className="px-1 text-xs font-medium text-fg">Gravar quando acontecer</legend>
      <p className="text-2xs text-fg-subtle">
        A câmera só grava quando acontece um destes registros, até o tempo de “Guardar depois” após
        o último, e envia direto ao armazenamento. Depende de a câmera enviar eventos (ONVIF ou
        Intelbras/Dahua).
      </p>
      {(errors['event.kinds'] ?? '') !== '' && (
        <p role="alert" className="text-xs text-crit">
          Escolha pelo menos um registro.
        </p>
      )}

      <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
        {eventTriggerGroups.map((g) => (
          <div key={g.label} role="group" aria-label={g.label}>
            <p className="mb-1 text-2xs font-medium tracking-wide text-fg-subtle uppercase">
              {g.label}
            </p>
            <div className="space-y-1">
              {g.kinds.map((k) => (
                <label key={k} className="flex items-center gap-2 text-[13px] text-fg">
                  <input
                    type="checkbox"
                    disabled={readOnly}
                    checked={value.kinds.includes(k)}
                    onChange={() => {
                      toggle(k);
                    }}
                    className="size-4 accent-[var(--color-accent-strong)]"
                  />
                  {kindLabels[k] ?? k}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-xs text-fg-muted">
          Guardar antes do registro
          <select
            disabled={readOnly}
            value={String(value.pre_seconds)}
            onChange={(e) => {
              onChange({ ...value, pre_seconds: Number(e.target.value) });
            }}
            className="h-8 rounded-md border border-line-strong bg-canvas px-1.5 text-[13px] text-fg"
          >
            {[...new Set([...preSecondsOptions, value.pre_seconds])]
              .sort((a, b) => a - b)
              .map((s) => (
                <option key={s} value={String(s)}>
                  {seconds(s)}
                </option>
              ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-fg-muted">
          Guardar depois
          <select
            disabled={readOnly}
            value={String(value.post_seconds)}
            onChange={(e) => {
              onChange({ ...value, post_seconds: Number(e.target.value) });
            }}
            className="h-8 rounded-md border border-line-strong bg-canvas px-1.5 text-[13px] text-fg"
          >
            {[...new Set([...postSecondsOptions, value.post_seconds])]
              .sort((a, b) => a - b)
              .map((s) => (
                <option key={s} value={String(s)}>
                  {seconds(s)}
                </option>
              ))}
          </select>
        </label>
      </div>
      <p className="text-2xs text-fg-subtle">
        Buscado no equipamento (DVR/NVR ou cartão da câmera) e guardado junto; se ele não tiver,
        grava só a partir do evento.
      </p>
    </fieldset>
  );
}
